import { HttpParams } from '@angular/common/http'
import { TimeRepresentation } from '../../app/common/components/hierarch-menu/timerep.model'
import { UiConstants } from '../../app/ui/constants/ui-constants'
import { PaginatedList, PaginatedSearchCriteria } from './base.model'
import { Employee } from './employee.model'
import { Month, Months } from './months.enum'
import { TimeCard } from './timecard.model'
import { TimesheetProject } from './timesheet-project.model'

// State should not be sent back to the backend.
// Backend needs to set the state based on the type of call.
export enum TimesheetState {
    TEMP = 0, // Has not been approved
    APPROVED = 1, // Has been approved and needs to be invoiced
    NO_INVOICE = 2, // Has been approved and does not need to be invoiced
    INVOICED = 3 // Has been approved and invoiced
}

export interface Timesheet extends TimeRepresentation {
    toInvoice: boolean
    invoiced: boolean
    sendToCustomer: boolean
    detailed: boolean
    state: TimesheetState
    month: Month
    year: number
    employee: Employee
    tag: string
    tags: Set<string>
    tagMetaGroup: string
    timesheetProject: TimesheetProject
    timeCards: Array<TimeCard>
}

export const calculateHours = (timesheet: Timesheet): void => {
    timesheet.secondsToInvoice = 0
    timesheet.secondsNoInvoice = 0
    if (timesheet.timeCards) {
        timesheet.timeCards.forEach((timeCard: TimeCard) => {
            timesheet.secondsToInvoice += timeCard.secondsToInvoice
            timesheet.secondsNoInvoice += timeCard.secondsNoInvoice
        })
    }
}

export const fromJSON = (json: any): Timesheet => {
    const result = {
        ...json,
        state:
            json.state === TimesheetState[TimesheetState.TEMP]
                ? TimesheetState[json.state]
                : json.state === TimesheetState[TimesheetState.APPROVED]
                ? json.toInvoice
                    ? json.invoiced
                        ? TimesheetState.INVOICED
                        : TimesheetState.APPROVED
                    : TimesheetState.NO_INVOICE
                : null,
        month: json.month,
        employee: json.employee ? Employee.fromJSON(json.employee) : null,
        timesheetProject: json.timesheetProject ? TimesheetProject.fromJSON(json.timesheetProject) : null,
        timeCards: json.timeCards ? json.timeCards.map((item: any) => TimeCard.fromJSON(item)) : null
    }
    if (result.timeCards !== undefined) {
        result.tag = result.timeCards[0].tags.size > 1 ? UiConstants.MultiTag : result.timeCards[0].tag
        result.tags = new Set<string>()
        result.timeCards.forEach((timecard: TimeCard) => {
            result.tag = result.tag === timecard.tag ? result.tag : UiConstants.MultiTag
            const tags: Array<string> = []
            if (result.tags !== undefined) {
                result.tags.forEach((tag: string) => {
                    tags.push(tag)
                })
                result.tags = new Set([...tags, ...timecard.tags])
            }
        })
    }
    calculateHours(result)

    return result
}

export class TimesheetGenCriteria {
    month?: Month
    year: number
    employeeId?: number
    timesheetProjectId?: number
    tag?: string
    includeArchived?: boolean
    toInvoice?: boolean
    invoiced?: boolean
    page?: number
    pageSize?: number

    constructor(
        month: Month | undefined,
        year: number,
        employeeId: number | undefined,
        timesheetProjectId: number | undefined,
        tag: string | undefined
    ) {
        this.month = month
        this.year = year
        this.employeeId = employeeId
        this.timesheetProjectId = timesheetProjectId
        this.tag = tag
    }
}

/**
 * Because we want to use the spread operator on TimesheetGenCriteria, we'll split this function from the class.
 */
export const getHttpParams = (timesheetCriteria: TimesheetGenCriteria): HttpParams => {
    let params = new HttpParams()

    if (timesheetCriteria.month !== undefined) {
        params = params.set('month', `${timesheetCriteria.month}`)
    }
    if (timesheetCriteria.year) {
        params = params.set('year', `${timesheetCriteria.year}`)
    }
    if (timesheetCriteria.employeeId) {
        params = params.set('employeeId', `${timesheetCriteria.employeeId}`)
    }
    if (timesheetCriteria.timesheetProjectId !== undefined) {
        params = params.set('timesheetProjectId', `${timesheetCriteria.timesheetProjectId}`)
    }
    if (timesheetCriteria.tag !== undefined) {
        params = params.set('tag', timesheetCriteria.tag)
    }
    if (timesheetCriteria.includeArchived) {
        params = params.set('includeArchived', `${timesheetCriteria.includeArchived}`)
    }

    return params
}

/**
 * Because we want to use the spread operator on TimesheetGenCriteria, we'll split this function from the class.
 */
export const toText = (timesheetCriteria: TimesheetGenCriteria): string => {
    let text = ''

    if (timesheetCriteria.month !== undefined) {
        text += ` - month: ${Months[timesheetCriteria.month as unknown as number]}`
    }

    if (timesheetCriteria.year) {
        text += ` - year: ${timesheetCriteria.year}`
    }

    if (timesheetCriteria.timesheetProjectId !== null) {
        text += ` - to timesheetProjectId: ${timesheetCriteria.timesheetProjectId}`
    }

    if (timesheetCriteria.tag !== null) {
        text += ` - tag: ${timesheetCriteria.tag}`
    }

    if (timesheetCriteria.employeeId) {
        text += ` - employeeId: ${timesheetCriteria.employeeId}`
    }

    if (timesheetCriteria.includeArchived) {
        text += ` - includeArchived: ${timesheetCriteria.includeArchived}`
    }

    return text
}

export class TimesheetSearchCriteria extends PaginatedSearchCriteria {
    month?: Month
    year?: number
    employeeId?: number
    timesheetProjectId?: number
    toInvoice?: boolean
    invoiced?: boolean
    includeArchived?: boolean
    tag?: string

    static fromJSON(json: any): TimesheetSearchCriteria {
        const result: TimesheetSearchCriteria = Object.create(TimesheetSearchCriteria.prototype)

        return { ...result, ...json }
    }

    constructor() {
        super()
    }

    httpParams(): HttpParams {
        let params = super.httpParams()

        if (this.month !== undefined) {
            params = params.set('month', `${this.month}`)
        }
        if (this.year) {
            params = params.set('year', `${this.year}`)
        }
        if (this.employeeId) {
            params = params.set('employeeId', `${this.employeeId}`)
        }
        if (this.toInvoice) {
            params = params.set('toInvoice', `${this.toInvoice}`)
        }
        if (this.invoiced !== undefined) {
            params = params.set('invoiced', `${this.invoiced}`)
        }
        if (this.tag) {
            params = params.set('tag', this.tag)
        }
        if (this.timesheetProjectId) {
            params = params.set('tag', `${this.timesheetProjectId}`)
        }

        if (this.includeArchived) {
            params = params.set('includeArchived', `${this.includeArchived}`)
        }

        return params
    }
}

export class TimesheetPaginatedList extends PaginatedList<Timesheet> {
    static fromJSON(json: any): TimesheetPaginatedList {
        const result: TimesheetPaginatedList = Object.create(TimesheetPaginatedList.prototype)

        return {
            result,
            ...json,
            items: json.items ? json.items.map(fromJSON) : null
        }
    }
}
